<template>
  <div class="bg-white rounded-lg border p-3">
    <h3 class="text-sm font-bold text-gray-800 mb-2">Enrollment History</h3>
    <div v-if="!records.length" class="text-sm text-gray-400 text-center py-4">
      No enrollment records found.
    </div>
    <DataTable v-else :value="records" size="small" stripedRows responsiveLayout="scroll" :rows="records.length">
      <Column header="Session" style="min-width: 120px">
        <template #body="{ data }">
          {{ data.academicCalendar?.session?.name ?? '—' }}
        </template>
      </Column>
      <Column header="Semester" style="min-width: 100px">
        <template #body="{ data }">
          {{ data.academicCalendar?.termStructure?.name ?? '—' }}
        </template>
      </Column>
      <Column header="Level" style="min-width: 80px">
        <template #body="{ data }">
          {{ data.classroomSection?.classroom?.name ?? '—' }}
        </template>
      </Column>
      <Column header="Programme" style="min-width: 150px">
        <template #body="{ data }">
          <span v-if="data.programme">{{ data.programme.program_name }} ({{ data.programme.degree }})</span>
          <span v-else class="text-gray-400">—</span>
        </template>
      </Column>
      <Column header="Status" style="min-width: 90px">
        <template #body="{ data }">
          <Tag :value="data.status" :severity="statusSeverity(data.status)" />
        </template>
      </Column>
      <Column header="Min/Max Units" style="min-width: 100px">
        <template #body="{ data }">
          {{ data.min_units_per_registration ?? '—' }} / {{ data.max_units_per_registration ?? '—' }}
        </template>
      </Column>
      <Column header="Actions" style="width: 80px" bodyClass="text-center">
        <template #body="{ data }">
          <div class="flex items-center gap-1 justify-center">
            <Button icon="pi pi-pencil" severity="warn" text rounded size="small"
              v-tooltip.top="'Edit'" @click="openEdit(data)" />
            <Button icon="pi pi-trash" severity="danger" text rounded size="small"
              v-tooltip.top="'De-enroll'" @click="confirmDeenroll(data)" />
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- Edit Enrollment Dialog -->
    <Dialog v-model:visible="showEditDialog" header="Edit Enrollment" modal :style="{ width: '440px' }">
      <div class="space-y-3 pt-2">
        <div class="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
          {{ editingRecord?.academicCalendar?.session?.name }} / {{ editingRecord?.academicCalendar?.termStructure?.name }}
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Status</label>
          <Select v-model="editForm.status" :options="statusOptions" optionLabel="label" optionValue="value"
            placeholder="Select status" size="small" class="w-full" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Level / Class</label>
          <ClassroomSectionSelect v-model="editForm.classroom_section_id" size="small"
            placeholder="Select level" className="w-full" :showClassroomsOnly="true" :emitSectionId="true" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Programme</label>
          <ProgrammeSelect v-model="editForm.programme_id" size="small"
            placeholder="Select programme" className="w-full" showClear />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Min Units</label>
            <InputNumber v-model="editForm.min_units" :min="0" size="small" class="w-full" placeholder="0" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Max Units</label>
            <InputNumber v-model="editForm.max_units" :min="0" size="small" class="w-full" placeholder="0" />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Entry Level</label>
            <InputText v-model="editForm.entry_level" size="small" class="w-full" placeholder="e.g. 200" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Min Graduation Units</label>
            <InputNumber v-model="editForm.min_graduation_units" :min="0" size="small" class="w-full" placeholder="Programme default" />
          </div>
        </div>
        <div v-if="editForm.classroom_section_id || editForm.programme_id" class="flex items-center gap-2 pt-1">
          <Checkbox v-model="editForm.cascade" :binary="true" inputId="enrollCascade" />
          <label for="enrollCascade" class="text-xs text-gray-700 cursor-pointer">
            Also update course registrations (programme &amp; level)
          </label>
        </div>
      </div>
      <template #footer>
        <Button size="small" label="Cancel" text severity="secondary" @click="showEditDialog = false" />
        <Button size="small" label="Save" icon="pi pi-check" :loading="saving" @click="saveEdit" />
      </template>
    </Dialog>

    <!-- De-enroll Confirm -->
    <Dialog v-model:visible="showDeenrollConfirm" header="De-enroll from Calendar" modal :style="{ width: '400px' }">
      <p class="text-sm text-gray-600">
        Remove this student from
        <strong>{{ deenrollingRecord?.academicCalendar?.session?.name }} / {{ deenrollingRecord?.academicCalendar?.termStructure?.name }}</strong>?
      </p>
      <div class="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 mt-2">
        <i class="pi pi-exclamation-triangle mr-1"></i>
        This action cannot be undone.
      </div>
      <template #footer>
        <Button size="small" label="Cancel" text severity="secondary" @click="showDeenrollConfirm = false" />
        <Button size="small" label="De-enroll" icon="pi pi-trash" severity="danger" :loading="deenrolling" @click="executeDeenroll" />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import gql from 'graphql-tag';
import ClassroomSectionSelect from '@/components/common/ClassroomSectionSelect.vue';
import ProgrammeSelect from '@/components/common/ProgrammeSelect.vue';
import type { StudentAcademicCalendar } from '@/types/composition-functions';
import { getErrorMessage } from '@/utils/error';

const props = defineProps<{
  records: StudentAcademicCalendar[];
  studentId: number;
}>();

const emit = defineEmits<{ refresh: [] }>();
const toast = useToast();

const statusOptions = [
  { label: 'Enrolled', value: 'enrolled' },
  { label: 'Promoted', value: 'promoted' },
  { label: 'Graduated', value: 'graduated' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Withdrawn', value: 'withdrawn' },
  { label: 'Transferred', value: 'transferred' },
];

function statusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
  switch (status?.toLowerCase()) {
  case 'enrolled': return 'success';
  case 'promoted': return 'info';
  case 'graduated': return 'success';
  case 'suspended': return 'warn';
  case 'withdrawn': return 'danger';
  case 'transferred': return 'secondary';
  default: return 'secondary';
  }
}

// --- Edit ---
const showEditDialog = ref(false);
const editingRecord = ref<StudentAcademicCalendar | null>(null);
const editForm = reactive({
  status: '',
  classroom_section_id: '',
  programme_id: '',
  min_units: null as number | null,
  max_units: null as number | null,
  entry_level: '' as string,
  min_graduation_units: null as number | null,
  cascade: false,
});

const UPDATE_ENROLLMENT = gql`
  mutation BulkUpdateStudents($input: BulkUpdateStudentsInput!) {
    bulkUpdateStudents(input: $input) { success message }
  }
`;
const UPDATE_STATUS = gql`
  mutation UpdateStudentStatus($input: UpdateStudentStatusInput!) {
    updateStudentStatus(input: $input) { success message }
  }
`;
const { mutate: updateMutate, loading: saving } = useMutation(UPDATE_ENROLLMENT);
const { mutate: statusMutate } = useMutation(UPDATE_STATUS);

function openEdit(record: StudentAcademicCalendar) {
  editingRecord.value = record;
  editForm.status = record.status ?? '';
  editForm.classroom_section_id = record.classroomSection?.id ? String(record.classroomSection.id) : '';
  editForm.programme_id = record.programme_id ? String(record.programme_id) : '';
  editForm.min_units = record.min_units_per_registration;
  editForm.max_units = record.max_units_per_registration;
  editForm.entry_level = record.entry_level ?? '';
  editForm.min_graduation_units = record.min_graduation_units ?? null;
  editForm.cascade = false;
  showEditDialog.value = true;
}

async function saveEdit() {
  if (!editingRecord.value) return;
  const calId = String(editingRecord.value.academic_calendar_id);
  const studentId = String(props.studentId);

  try {
    // Update status if changed
    if (editForm.status && editForm.status !== editingRecord.value.status) {
      const statusRes = await statusMutate({
        input: {
          student_ids: [studentId],
          status: editForm.status,
          academic_calendar_id: calId,
        },
      });
      const statusData = statusRes?.data?.updateStudentStatus;
      if (!statusData?.success) {
        toast.add({ severity: 'error', summary: 'Update Failed', detail: statusData?.message || 'Failed to update status.', life: 5000 });
        return;
      }
    }

    // Update placement fields
    const input: Record<string, unknown> = {
      student_ids: [studentId],
      academic_calendar_id: calId,
    };
    if (editForm.classroom_section_id) input.classroom_section_id = editForm.classroom_section_id;
    if (editForm.programme_id) input.programme_id = editForm.programme_id;
    if (editForm.min_units !== null) input.min_units_per_registration = editForm.min_units;
    if (editForm.max_units !== null) input.max_units_per_registration = editForm.max_units;
    if (editForm.entry_level) input.entry_level = editForm.entry_level;
    if (editForm.min_graduation_units !== null) input.min_graduation_units = editForm.min_graduation_units;
    if (editForm.cascade) input.cascade_to_registrations = true;

    const updateRes = await updateMutate({ input });
    const updateData = updateRes?.data?.bulkUpdateStudents;
    if (!updateData?.success) {
      toast.add({ severity: 'error', summary: 'Update Failed', detail: updateData?.message || 'Failed to update enrollment.', life: 5000 });
      return;
    }

    toast.add({ severity: 'success', summary: 'Updated', detail: 'Enrollment updated.', life: 3000 });
    showEditDialog.value = false;
    emit('refresh');
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: getErrorMessage(e), life: 5000 });
  }
}

// --- De-enroll ---
const showDeenrollConfirm = ref(false);
const deenrollingRecord = ref<StudentAcademicCalendar | null>(null);

const DEENROLL = gql`
  mutation DeenrollStudents($input: DeenrollStudentsInput!) {
    deenrollStudents(input: $input) { success message }
  }
`;
const { mutate: deenrollMutate, loading: deenrolling } = useMutation(DEENROLL);

function confirmDeenroll(record: StudentAcademicCalendar) {
  deenrollingRecord.value = record;
  showDeenrollConfirm.value = true;
}

async function executeDeenroll() {
  if (!deenrollingRecord.value) return;
  try {
    const res = await deenrollMutate({
      input: {
        student_ids: [String(props.studentId)],
        academic_calendar_id: String(deenrollingRecord.value.academic_calendar_id),
      },
    });
    const data = res?.data?.deenrollStudents;
    if (data?.success) {
      toast.add({ severity: 'success', summary: 'De-enrolled', detail: data.message, life: 3000 });
      showDeenrollConfirm.value = false;
      emit('refresh');
    }
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: getErrorMessage(e), life: 5000 });
  }
}
</script>
